"use client";

import type { BoutiqueTransitionProps } from "@/app/directories/shop/[clientSlug]/types";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { BoutiqueCircle } from "../boutiqueIntro/BoutiqueSvg";

const roundedDiamondPath = `
<svg width='500' height='500' viewBox='0 0 500 500' fill='none' xmlns='http://www.w3.org/2000/svg'>
  <path d='
    M250 10
    Q260 10 270 20
    L480 230
    Q490 240 490 250
    Q490 260 480 270
    L270 480
    Q260 490 250 490
    Q240 490 230 480
    L20 270
    Q10 260 10 250
    Q10 240 20 230
    L230 20
    Q240 10 250 10
  ' fill='black'/>
</svg>
`
  .replace(/\s+/g, " ")
  .trim();

export const BoutqiueTransitionComponent = ({
  props,
}: { props: BoutiqueTransitionProps }) => {
  const { title, subtitle, bgImgs } = props;
  // Create a reference for the component container
  const containerRef = useRef(null);

  // Track scroll progress within the component
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["0.5 end", "end start"],
  });

  const offsetProgress = 0; // 200 / window.innerHeight;

  // Adjust animation ranges to start after our offset point
  // We map 0-1 scroll progress to our desired animation ranges
  const opacity = useTransform(
    scrollYProgress,
    [offsetProgress, offsetProgress + 0.2, 0.8, 1], // Start after offset
    [0, 1, 1, 0],
  );

  const leftX = useTransform(
    scrollYProgress,
    [offsetProgress, offsetProgress + 0.2, 0.8, 1],
    [-300, 0, 0, -300],
  );

  const scale = useTransform(
    scrollYProgress,
    [offsetProgress, offsetProgress + 0.2, 0.8, 1],
    [0.95, 1, 1, 0.95],
  );

  // Create staggered Y positions for each image
  const imageY1 = useTransform(
    scrollYProgress,
    [0, 0.2, 0.8, 1],
    [-60, 0, 0, 60],
  );

  const imageY2 = useTransform(
    scrollYProgress,
    [0, 0.2, 0.8, 1],
    [-40, 20, 20, 80],
  );

  const imageY3 = useTransform(
    scrollYProgress,
    [0, 0.2, 0.8, 1],
    [-20, 40, 40, 100],
  );

  const rightX = useTransform(
    scrollYProgress,
    [offsetProgress, offsetProgress + 0.2, 0.8, 1],
    [300, 0, 0, 300],
  );

  const circleScale = useTransform(
    scrollYProgress,
    [offsetProgress + 0.1, offsetProgress + 0.3, 0.7, 0.9],
    [0, 1, 1, 0],
  );

  const svgMask = `data:image/svg+xml,${encodeURIComponent(roundedDiamondPath)}`;

  return (
    <motion.div
      ref={containerRef}
      className="flex flex-row lg:h-[90vh] p-8 py-10 lg:px-[12rem] sm:px-0 relative w-full"
      style={{ opacity }}
    >
      {/* Left section with title and circle */}
      <motion.div className="flex flex-col gap-4" style={{ x: leftX }}>
        <p className="config-h2">{title}</p>
        <BoutiqueCircle />
      </motion.div>

      <div className="relative w-[1px] h-full">
        <div className="absolute top-0 left-0 transform -translate-x-1/2 -translate-y-1/2">
          {/* First image (back) */}
          <motion.div
            className="absolute w-full h-full lg:w-[500px] lg:h-[500px] origin-center"
            style={{
              scale,
              y: imageY1,
              zIndex: 1,
              WebkitMaskImage: `url("${svgMask}")`,
              WebkitMaskSize: "100% 100%",
              maskImage: `url("${svgMask}")`,
              maskSize: "100% 100%",
            }}
          >
            <div className="relative w-full h-full overflow-hidden">
              <img
                src={bgImgs[0]}
                alt="background"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          {/* Second image (middle) */}
          <motion.div
            className="absolute w-full h-full lg:w-[500px] lg:h-[500px] origin-center"
            style={{
              scale,
              y: imageY2,
              zIndex: 2,
              WebkitMaskImage: `url("${svgMask}")`,
              WebkitMaskSize: "100% 100%",
              maskImage: `url("${svgMask}")`,
              maskSize: "100% 100%",
            }}
          >
            <div className="relative w-full h-full overflow-hidden">
              <img
                src={bgImgs[1]}
                alt="middle"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          {/* Third image (front) */}
          <motion.div
            className="absolute w-full h-full lg:w-[500px] lg:h-[500px] origin-center"
            style={{
              scale,
              y: imageY3,
              zIndex: 3,
              WebkitMaskImage: `url("${svgMask}")`,
              WebkitMaskSize: "100% 100%",
              maskImage: `url("${svgMask}")`,
              maskSize: "100% 100%",
            }}
          >
            <div className="relative w-full h-full overflow-hidden">
              <img
                src={bgImgs[2]}
                alt="foreground"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        className="w-4/5 p-6 flex flex-col h-full justify-end items-end"
        style={{ x: rightX }}
      >
        <p className="config-p2">{subtitle}</p>
      </motion.div>
    </motion.div>
  );
};
